import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import './index.css';
import App from './App';
import { ThemeProvider } from '@/theme/ThemeProvider';
import { store } from '@/store';
import { FEATURES } from '@/constants/env';
import { initSentry } from '@/telemetry/sentry';
import { initOtel } from '@/telemetry/otel';
import { registerServiceWorker } from '@/pwa/register';
import { initI18n } from '@/i18n';

// Initialize OpenTelemetry if enabled (must be first for instrumentation)
if (FEATURES.ENABLE_OTEL) {
  initOtel();
}

// Initialize Sentry if enabled
if (FEATURES.ENABLE_SENTRY) {
  initSentry();
}

// Register PWA service worker if enabled
if (FEATURES.ENABLE_PWA) {
  registerServiceWorker({
    onNeedRefresh() {
      // Show update notification to user
      console.log('[PWA] New version available! Please refresh to update.');
    },
    onOfflineReady() {
      console.log('[PWA] App is ready for offline use.');
    },
  });
}

// Loading screen component for i18n initialization
const LoadingScreen = () => (
  <div className="flex h-screen w-screen items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

// Initialize i18n before rendering
initI18n().then(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <Suspense fallback={<LoadingScreen />}>
        <ThemeProvider>
          <Provider store={store}>
            <App />
          </Provider>
        </ThemeProvider>
      </Suspense>
    </StrictMode>
  );
});
